const generation = {
    system: 'OpenAI Codex AI assistant',
    date: '18 August 2026',
    status: 'Human review pending',
    note: 'The AI synthesized the content from the listed biblical, scholarly, and ecclesial sources. Associations describe broad affinities, not formal endorsements by every person or tradition represented.',
};

const source = (id, author, title, detail, href) => ({ id, author, title, detail, href });

export const practicePerspectives = [
    {
        slug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
        category: 'Biblical Interpretation',
        question: 'How should Christians apply culturally specific apostolic commands?',
        shortTitle: 'Applying culturally specific commands',
        summary: 'Christians agree that apostolic teaching is authoritative, but differ over how commands expressed through first-century gestures, dress, household patterns, and worship practices should be obeyed today. The central question is whether faithfulness normally retains the commanded form, preserves its purpose through a new form, or receives it through the church’s living tradition.',
        introduction: 'The alternatives are not simply literal obedience versus dismissal. Every reading must identify what the text commands, why it commands it, how the action communicated in its original setting, and what would count as recognisable obedience in another culture. Different traditions assign different weight to the wording of the command, its moral purpose, canonical context, and historic reception by the church.',
        seoDescription: 'Compare Christian approaches to applying culturally specific biblical commands: retaining their form, adapting their expression, or receiving them through tradition.',
        lastModified: '2026-08-18',
        keyTexts: [
            { label: 'Romans 16:16', href: '/read/Rom.16/#Rom.16.16' },
            { label: 'John 13:14–15', href: '/read/John.13/#John.13.14-John.13.15' },
            { label: '1 Corinthians 11:2–16', href: '/read/1Cor.11/#1Cor.11.2-1Cor.11.16' },
        ],
        readings: [
            {
                id: 'retain-commanded-form',
                title: 'Keep the command in its original form',
                thesis: 'A repeated apostolic command should ordinarily be practised in its stated form unless Scripture itself limits or transforms it.',
                accent: 'navy',
                badges: ['Conservative', 'Anabaptist', 'Brethren', 'Plain Reading'],
                summary: 'This approach begins with the action actually named by the text. If the apostles command a kiss, footwashing, head covering, or another embodied practice, the burden of proof lies with replacing it rather than retaining it. Culture helps explain how to practise the action appropriately, but it should not quietly turn a concrete command into a general attitude. Historic Brethren practice illustrates this instinct by retaining embodied ordinances as visible acts of discipleship and fellowship.',
                contribution: 'It resists interpretations that affirm a command’s principle while removing every demanding or distinctive practice through which that principle was expressed. It also keeps obedience public, bodily, and accountable to the wording of Scripture.',
                caution: 'Not every apostolic instruction has the same scope, and even an unchanged action can communicate something different in another setting. This approach must still distinguish universal commands from local directions and address consent, safeguarding, and cultural intelligibility.',
                supporters: [
                    { name: 'Brethren embodied-practice tradition', sourceId: 'brethren-love-context' },
                ],
                predicates: [
                    {
                        label: 'The repeated holy-kiss command retains its physical form',
                        topicSlug: 'should-christians-greet-one-another-with-a-holy-kiss',
                        readingId: 'keep-the-physical-kiss',
                        linked: true,
                    },
                    {
                        label: 'Faithful mission remains accountable to concrete apostolic limits',
                        topicSlug: 'was-pauls-mission-strategy-genuine-or-pragmatic',
                        readingId: 'genuine-flexibility',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'preserve-purpose-adapt-form', linked: true },
                    { type: 'overlaps with', target: 'ecclesial-reception', linked: true },
                ],
            },
            {
                id: 'preserve-purpose-adapt-form',
                title: 'Keep the purpose while adapting the form',
                thesis: 'The command’s theological and moral purpose remains binding even when another action communicates it more faithfully in a different culture.',
                accent: 'teal',
                badges: ['Moderate', 'Contextual', 'Missional', 'Pastoral'],
                summary: 'This reading distinguishes the enduring purpose of a command from the culturally located form through which that purpose was first enacted. Interpretation asks what the practice communicated—welcome, modesty, equality, service, honour, or reconciliation—and seeks a present action that communicates the same reality rather than merely reproducing its appearance. Adaptation is therefore an act of translation, not permission to ignore inconvenient instructions.',
                contribution: 'It takes historical context and contemporary communication seriously. A gesture that is clear and honourable in one culture may be confusing, intimate, or offensive in another, so thoughtful adaptation can protect the command’s intended meaning.',
                caution: 'The proposed principle can become so general that almost any modern behaviour appears acceptable. This approach needs transparent reasons for identifying the purpose and a concrete practice that remains visible, costly, and accountable to the text.',
                supporters: [
                    { name: 'John Ellington', sourceId: 'ellington-kissing' },
                    { name: 'Pope Leo XIV', sourceId: 'leo-scripture-human-words' },
                ],
                predicates: [
                    {
                        label: 'The holy kiss’s purpose can be retained through a culturally fitting greeting',
                        topicSlug: 'should-christians-greet-one-another-with-a-holy-kiss',
                        readingId: 'adapt-the-greeting',
                        linked: true,
                    },
                    {
                        label: 'Mission may adapt practice without changing the gospel’s governing commitments',
                        topicSlug: 'was-pauls-mission-strategy-genuine-or-pragmatic',
                        readingId: 'pragmatic-accommodation',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'retain-commanded-form', linked: true },
                    { type: 'overlaps with', target: 'ecclesial-reception', linked: true },
                ],
            },
            {
                id: 'ecclesial-reception',
                title: 'Follow the command through living tradition',
                thesis: 'Apostolic practices are faithfully understood as Scripture is received, prayed, taught, and enacted within the historic worship and discernment of the church.',
                accent: 'amber',
                badges: ['Catholic', 'Orthodox', 'Liturgical', 'Ecumenical'],
                summary: 'This approach reads individual commands within Scripture’s canonical unity and the church’s continuing life. A practice may develop liturgically while preserving its apostolic substance: the holy kiss, for example, can be received as the church’s sign of peace. Tradition does not stand above Scripture on this account; it provides a communal history of interpretation through which the church distinguishes durable meaning from variable discipline and expression.',
                contribution: 'It avoids treating interpretation as an isolated decision made anew by each reader or generation. Historic worship can preserve embodied meanings—peace, confession, communion, service—that a purely conceptual interpretation might lose.',
                caution: 'Traditions can preserve cultural assumptions as well as apostolic wisdom, and churches disagree about which developments are faithful. This approach must show how a received practice remains accountable to Scripture and open to reform.',
                supporters: [
                    { name: 'Second Vatican Council', sourceId: 'dei-verbum' },
                    { name: 'Orthodox Church in America', sourceId: 'oca-love-faith' },
                ],
                predicates: [
                    {
                        label: 'The holy kiss is received corporately as the liturgical sign of peace',
                        topicSlug: 'should-christians-greet-one-another-with-a-holy-kiss',
                        readingId: 'liturgical-sign-of-peace',
                        linked: true,
                    },
                    {
                        label: 'Shared worship can embody the church’s reconciled fellowship',
                        topicSlug: 'why-did-table-fellowship-matter',
                        readingId: 'gospel-enacted',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'receives', target: 'retain-commanded-form', linked: true },
                    { type: 'incorporates', target: 'preserve-purpose-adapt-form', linked: true },
                ],
            },
        ],
        sources: [
            source(
                'brethren-love-context',
                'Church of the Brethren',
                '‘Love in Context’ equipping session offers biblical and historical reflections on the meaning of love',
                'A denominational account of embodied Brethren practices, including the holy kiss and its role in repairing relationships.',
                'https://www.brethren.org/news/2025/love-in-context-equipping-session/',
            ),
            source(
                'ellington-kissing',
                'John Ellington',
                'Kissing in the Bible: Form and Meaning',
                'A Bible translation study showing how culturally unfamiliar gestures require careful interpretation without distorting their biblical meaning.',
                'https://translation.bible/wp-content/uploads/2024/06/ellington-1990-kissing-in-the-bible-form-and-meaning.pdf',
            ),
            source(
                'leo-scripture-human-words',
                'Pope Leo XIV',
                'General Audience: The Sacred Scripture, the Word of God in Human Words',
                'An official Catholic reflection on literary and historical context and the need to proclaim Scripture in language capable of reaching each age.',
                'https://www.vatican.va/content/leo-xiv/en/audiences/2026/documents/20260204-udienza-generale.html',
            ),
            source(
                'dei-verbum',
                'Second Vatican Council',
                'Dogmatic Constitution on Divine Revelation: Dei Verbum',
                'An official account of Scripture, apostolic tradition, historical context, and interpretation within the life of the church.',
                'https://www.vatican.va/archive/hist_councils/ii_vatican_council/documents/vat-ii_const_19651118_dei-verbum_en.html',
            ),
            source(
                'oca-love-faith',
                'Orthodox Church in America',
                'Love and Faith',
                'An Orthodox explanation of how the apostolic kiss of peace is received within the Divine Liturgy.',
                'https://www.oca.org/orthodoxy/the-orthodox-faith/worship/the-divine-liturgy/love-and-faith',
            ),
        ],
        generation,
    },
    {
        slug: 'how-should-holiness-be-recognised',
        category: 'Christian Formation',
        question: 'How should holiness be recognised?',
        shortTitle: 'Recognising holiness',
        summary: 'Christian traditions agree that God alone knows the heart and that holiness is produced by grace, yet they describe its visible shape in different ways. Some emphasise durable fruit and obedience, some Spirit-empowered life and witness, some participation in the church’s sacramental and ascetic life, and some the inseparability of personal and social holiness.',
        introduction: 'Recognising holiness is not the same as declaring a person’s final standing before God. It is the more limited task of discerning whether claims of maturity, spiritual experience, leadership, or sanctification are accompanied by the character and practices Scripture associates with life in Christ. The readings differ mainly over which signs deserve the greatest weight and how the church should test them over time.',
        seoDescription: 'Compare Christian views on recognising holiness through spiritual fruit, Spirit-empowered life, church participation, and personal and social love.',
        lastModified: '2026-08-18',
        keyTexts: [
            { label: 'Matthew 7:15–23', href: '/read/Matt.7/#Matt.7.15-Matt.7.23' },
            { label: 'Galatians 5:16–26', href: '/read/Gal.5/#Gal.5.16-Gal.5.26' },
            { label: '1 Corinthians 13:1–13', href: '/read/1Cor.13/#1Cor.13.1-1Cor.13.13' },
            { label: 'James 2:14–26', href: '/read/Jas.2/#Jas.2.14-Jas.2.26' },
        ],
        readings: [
            {
                id: 'fruit-and-obedience',
                title: 'Holiness is shown by lasting fruit and faith',
                thesis: 'Holiness is recognised over time through repentance, Christlike character, and good works that arise as the fruit rather than the basis of saving faith.',
                accent: 'navy',
                badges: ['Reformed', 'Evangelical', 'Conservative'],
                summary: 'This reading gives priority to the ordinary evidence named in Scripture: love, self-control, truthfulness, mercy, perseverance, resistance to sin, and obedience to God’s commands. No single achievement proves holiness, and believers remain imperfect, but a living faith becomes publicly recognisable through a sustained direction of life shaped by the Spirit.',
                contribution: 'It provides concrete tests for spiritual claims while protecting grace from becoming a reward for moral performance. Long-term character receives more weight than reputation, office, eloquence, or a dramatic experience.',
                caution: 'External conduct can be performed or selectively measured, and communities may confuse their own social norms with biblical holiness. Discernment must remain patient, humble, and attentive to hidden forms of pride, coercion, or injustice.',
                supporters: [
                    { name: 'Westminster Assembly', sourceId: 'westminster-good-works' },
                ],
                predicates: [
                    {
                        label: 'Sanctification normally grows progressively through the Spirit’s work',
                        topicSlug: 'what-is-sanctification',
                        readingId: 'progressive-holiness',
                        linked: true,
                    },
                    {
                        label: 'Good works are evidence of grace rather than the basis of justification',
                        topicSlug: 'what-does-paul-mean-by-justification',
                        readingId: 'forensic-verdict',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'spirit-empowered-life', linked: true },
                    { type: 'overlaps with', target: 'personal-and-social-love', linked: true },
                    {
                        type: 'depends on',
                        label: 'What is holiness?',
                        topicSlug: 'what-is-holiness',
                        readingId: 'gods-holy-identity',
                        linked: true,
                    },
                ],
            },
            {
                id: 'spirit-empowered-life',
                title: 'The Spirit empowers holy life and witness',
                thesis: 'Holiness is recognised as a person’s new identity in Christ becomes visible through Spirit-governed obedience, worship, gifts, and witness.',
                accent: 'red',
                badges: ['Pentecostal', 'Charismatic', 'Evangelical'],
                summary: 'Pentecostal and charismatic readings expect the Holy Spirit to act recognisably in Christian life. Spiritual gifts and experiences can belong to that witness, but they are not substitutes for sanctification: the same Spirit directs the whole person away from evil and toward obedience, service, love, and mission. Holiness is therefore both received identity and observable Spirit-shaped life.',
                contribution: 'It preserves expectancy for God’s active presence and refuses to reduce holiness to unaided moral improvement. It also connects inward renewal with public witness and the exercise of gifts for the good of others.',
                caution: 'Visible gifts, emotional intensity, or confident claims of divine guidance can be mistaken for mature character. Experiences must be tested by Scripture, the fruit of the Spirit, accountable community, and their treatment of vulnerable people.',
                supporters: [
                    { name: 'Assemblies of God (USA)', sourceId: 'ag-fundamental-truths' },
                ],
                predicates: [
                    {
                        label: 'Believers are already set apart and continue becoming holy',
                        topicSlug: 'what-is-sanctification',
                        readingId: 'definitive-and-progressive',
                        linked: true,
                    },
                    {
                        label: 'Union with Christ grounds a genuinely transformed life',
                        topicSlug: 'how-does-union-with-christ-relate-to-justification',
                        readingId: 'union-as-foundation',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'extends', target: 'fruit-and-obedience', linked: true },
                    { type: 'overlaps with', target: 'participation-and-communion', linked: true },
                ],
            },
            {
                id: 'participation-and-communion',
                title: 'Holiness grows through communion with God',
                thesis: 'Holiness is recognised through growing communion with God and neighbour as grace forms a person through worship, sacrament, prayer, repentance, ascetic practice, and mercy.',
                accent: 'teal',
                badges: ['Catholic', 'Orthodox', 'Liturgical', 'Ecumenical'],
                summary: 'This reading treats holiness as participation in divine life rather than a checklist of isolated virtues. The person is healed and conformed to Christ within the worshipping body, receiving grace and learning love through practices that join doctrine, body, community, and service. Recognition therefore attends to a whole pattern of communion, humility, repentance, and mercy.',
                contribution: 'It makes holiness communal and embodied and gives the church’s ordinary practices a formative role. It also resists separating private character from participation in Christ’s body and care for other people.',
                caution: 'Formal participation can coexist with serious failure, and outward conformity to ecclesial practice does not reveal the heart. The view must preserve personal accountability and avoid treating institutional belonging as sufficient evidence of holiness.',
                supporters: [
                    { name: 'Catechism of the Catholic Church', sourceId: 'vatican-sanctifying-grace' },
                    { name: 'Orthodox Church in America', sourceId: 'oca-theosis' },
                ],
                predicates: [
                    {
                        label: 'Sanctification is participation in divine life through Christ and the Spirit',
                        topicSlug: 'what-is-sanctification',
                        readingId: 'participation-in-divine-life',
                        linked: true,
                    },
                    {
                        label: 'Righteousness includes a life received and participated in Christ',
                        topicSlug: 'is-christs-righteousness-counted-or-imparted',
                        readingId: 'participated-life',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'integrates', target: 'fruit-and-obedience', linked: true },
                    { type: 'qualifies', target: 'spirit-empowered-life', linked: true },
                ],
            },
            {
                id: 'personal-and-social-love',
                title: 'Holiness shapes personal and social life',
                thesis: 'Holiness is recognised in perfected love that joins personal devotion and character to accountable fellowship, mercy, justice, and service.',
                accent: 'amber',
                badges: ['Wesleyan', 'Methodist', 'Holiness', 'Progressive'],
                summary: 'Wesleyan traditions resist a holiness that remains private. Love for God becomes visible in love of neighbour, disciplined habits, mutual accountability, care for people in need, and action against conditions that diminish human dignity. A holy life is personal without being individualistic and social without reducing salvation to activism.',
                contribution: 'It asks whether spiritual maturity changes both intimate conduct and public relationships. This exposes the limits of piety that leaves exploitation, prejudice, neglect, or loveless community life untouched.',
                caution: 'Communities can identify holiness too closely with a particular reform programme or imply that visible activism proves inward maturity. Social witness and personal transformation must continue to correct and sustain one another.',
                supporters: [
                    { name: 'The United Methodist Church', sourceId: 'umc-wesleyan-heritage' },
                ],
                predicates: [
                    {
                        label: 'Entire sanctification is ordered toward perfect love of God and neighbour',
                        topicSlug: 'what-is-sanctification',
                        readingId: 'perfect-love',
                        linked: true,
                    },
                    {
                        label: 'God’s instruction continues to witness to faithful covenant life',
                        topicSlug: 'what-can-the-law-do-if-it-cannot-justify',
                        readingId: 'guides-covenant-life',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'extends', target: 'fruit-and-obedience', linked: true },
                    { type: 'complements', target: 'participation-and-communion', linked: true },
                ],
            },
        ],
        sources: [
            source(
                'westminster-good-works',
                'Westminster Assembly',
                'Westminster Confession of Faith, Chapter 16: Of Good Works',
                'A Reformed account of good works as Spirit-enabled fruits and evidences of living faith rather than its saving basis.',
                'https://opc.org/WCF-WIP.html',
            ),
            source(
                'ag-fundamental-truths',
                'Assemblies of God (USA)',
                'Statement of Fundamental Truths',
                'An official Pentecostal statement connecting sanctification with identification in Christ and a life yielded to the Holy Spirit.',
                'https://ag.org/en/Beliefs/Statement-of-Fundamental-Truths',
            ),
            source(
                'vatican-sanctifying-grace',
                'Catechism of the Catholic Church',
                'In Brief: Grace and Justification',
                'A Catholic summary of sanctifying grace, inner renewal, growth, and the perfection of charity.',
                'https://www.vatican.va/content/catechism/en/part_three/section_one/chapter_three/article_2/in_brief.html',
            ),
            source(
                'oca-theosis',
                'Orthodox Church in America',
                'The Orthodox Faith: Theosis',
                'An Orthodox account of holiness as participation in God’s life and transformation in Christ.',
                'https://www.oca.org/cdn/PDFs/2023-0609-EOCB.pdf',
            ),
            source(
                'umc-wesleyan-heritage',
                'The United Methodist Church',
                'Our Wesleyan Heritage',
                'A Methodist account of holiness of heart and life, grace, accountable community, and social holiness.',
                'https://www.umc.org/en/content/our-wesleyan-heritage',
            ),
        ],
        generation,
    },
    {
        slug: 'when-does-the-bible-permit-divorce-and-remarriage',
        category: 'Marriage and Christian Ethics',
        question: 'When does the Bible permit divorce and remarriage?',
        shortTitle: 'Biblical grounds for divorce and remarriage',
        summary: 'Christians broadly agree that marriage is intended as a lifelong covenant and that reconciliation should be sought where it is safe and truthful. They disagree over whether divorce ends the marriage bond, which circumstances may justify it, and when an innocent divorced spouse is free to remarry.',
        introduction: 'The debate turns especially on Jesus’ exception for sexual immorality, Paul’s statement that an abandoned believer is “not bound”, the Old Testament background to marital obligations, and the church’s authority to recognise a marriage or its breakdown. It is important to distinguish immediate separation for safety, civil divorce, the theological status of the marriage, and permission to remarry. No interpretation requires a person or children to remain in immediate danger while those questions are discerned.',
        seoDescription: 'Compare Christian views on when Scripture permits divorce and remarriage, including adultery, abandonment, abuse, indissolubility, and Orthodox economy.',
        lastModified: '2026-08-18',
        keyTexts: [
            { label: 'Deuteronomy 24:1–4', href: '/read/Deut.24/#Deut.24.1-Deut.24.4' },
            { label: 'Matthew 5:31–32', href: '/read/Matt.5/#Matt.5.31-Matt.5.32' },
            { label: 'Matthew 19:3–12', href: '/read/Matt.19/#Matt.19.3-Matt.19.12' },
            { label: 'Mark 10:2–12', href: '/read/Mark.10/#Mark.10.2-Mark.10.12' },
            { label: '1 Corinthians 7:10–16', href: '/read/1Cor.7/#1Cor.7.10-1Cor.7.16' },
        ],
        readings: [
            {
                id: 'no-remarriage-while-spouse-lives',
                title: 'Remarriage is not permitted while a spouse lives',
                thesis: 'Separation or civil divorce may sometimes be necessary, but the first marriage remains binding before God until death, so neither spouse is free to remarry while the other lives.',
                accent: 'navy',
                badges: ['Evangelical', 'Conservative', 'Marriage Permanence'],
                summary: 'This reading gives controlling weight to Jesus’ unqualified sayings in Mark 10 and Luke 16, Paul’s direction to remain unmarried or be reconciled, and the statements that a spouse is bound while the other lives. Matthew’s exception is read as permitting divorce without necessarily permitting remarriage, or as addressing sexual immorality discovered during betrothal. Serious adultery, abandonment, or violence may make separation unavoidable, but do not themselves dissolve the one-flesh bond.',
                contribution: 'It treats Jesus’ prohibition as a radical protection of marriage rather than a list of minimum conditions for ending it. It also prevents the conduct of an offending spouse from automatically determining the permanence of the innocent spouse’s covenant.',
                caution: 'The view must explain why Matthew includes an exception and what Paul means when he says an abandoned believer is “not bound”. Its lifelong restriction on remarriage can place a profound burden on an innocent spouse and therefore requires substantial pastoral and communal support.',
                supporters: [
                    { name: 'John Piper', sourceId: 'piper-divorce-remarriage' },
                ],
                predicates: [
                    {
                        label: 'Direct apostolic commands retain their stated force unless Scripture clearly supplies an exception',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'retain-commanded-form',
                        linked: true,
                    },
                    {
                        label: 'Holiness may require costly, durable obedience rather than the removal of every hardship',
                        topicSlug: 'how-should-holiness-be-recognised',
                        readingId: 'fruit-and-obedience',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'adultery-and-desertion', linked: true },
                    { type: 'contrasts with', target: 'covenant-breaking-including-abuse', linked: true },
                    { type: 'overlaps with', target: 'valid-sacramental-bond-indissoluble', linked: true },
                ],
            },
            {
                id: 'valid-sacramental-bond-indissoluble',
                title: 'A valid sacramental marriage cannot be dissolved',
                thesis: 'A valid, consummated sacramental marriage cannot be dissolved by divorce; separation or civil divorce may protect people and rights, but remarriage is not possible while the bond remains.',
                accent: 'teal',
                badges: ['Catholic', 'Sacramental', 'Conservative'],
                summary: 'Catholic teaching distinguishes the civil end of shared life from the continuing marriage bond. Physical separation can be legitimate, and civil divorce may be tolerated when it is the only practical way to secure safety, legal rights, or care of children. A declaration of nullity does not dissolve a marriage but judges that a valid bond was never formed; Catholic canon law also recognises narrowly defined Pauline and Petrine privileges in particular non-sacramental cases.',
                contribution: 'It makes careful distinctions between safety, legal status, sacramental validity, and freedom to marry. The view also places the discernment of validity within an accountable ecclesial process rather than leaving it solely to private judgement.',
                caution: 'People may experience the distinction between a civil divorce and an enduring bond as remote from the practical reality of a destroyed marriage. The accessibility, duration, and consistency of nullity processes also shape whether the view’s pastoral safeguards work as intended.',
                supporters: [
                    { name: 'Catechism of the Catholic Church', sourceId: 'catechism-marriage-offences' },
                    { name: 'Joseph Ratzinger', sourceId: 'ratzinger-divorced-remarried' },
                ],
                predicates: [
                    {
                        label: 'Scripture is discerned within the church’s living sacramental and canonical tradition',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'ecclesial-reception',
                        linked: true,
                    },
                    {
                        label: 'Christian life is formed through participation and communion within the church',
                        topicSlug: 'how-should-holiness-be-recognised',
                        readingId: 'participation-and-communion',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'overlaps with', target: 'no-remarriage-while-spouse-lives', linked: true },
                    { type: 'contrasts with', target: 'adultery-and-desertion', linked: true },
                    { type: 'qualifies', target: 'orthodox-pastoral-economia', linked: true },
                ],
            },
            {
                id: 'adultery-and-desertion',
                title: 'Adultery and abandonment can permit divorce',
                thesis: 'Sexual unfaithfulness and wilful desertion can dissolve the marriage bond, permitting the innocent spouse to divorce and remarry through an orderly church and civil process.',
                accent: 'red',
                badges: ['Reformed', 'Evangelical', 'Conservative'],
                summary: 'This classical Protestant reading takes Matthew’s exception to permit divorce and remarriage after sexual immorality and understands the abandoned believer who is “not bound” in 1 Corinthians 7 as free from the marriage. Divorce remains a last resort rather than a command: repentance and reconciliation should be pursued where possible, while public church and civil processes test whether the stated grounds are real and irremediable.',
                contribution: 'It holds together the lifelong norm of marriage and explicit biblical exceptions. It also recognises a meaningful distinction between the spouse who breaks the covenant and the innocent spouse who cannot unilaterally restore it.',
                caution: 'Interpreters dispute whether “not bound” includes permission to remarry and whether Jesus’ exception modifies the whole prohibition or only the guilt attached to divorce. The categories can also become legalistic if churches focus on proving a ground rather than truth, safety, repentance, and care.',
                supporters: [
                    { name: 'Westminster Assembly', sourceId: 'westminster-marriage-divorce' },
                ],
                predicates: [
                    {
                        label: 'Jesus fulfils and authoritatively interprets the Mosaic regulation of divorce',
                        topicSlug: 'how-does-the-old-covenant-relate-to-the-new',
                        readingId: 'fulfilled-and-transformed',
                        linked: true,
                    },
                    {
                        label: 'Biblical exceptions should retain their concrete force in church practice',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'retain-commanded-form',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'no-remarriage-while-spouse-lives', linked: true },
                    { type: 'contrasts with', target: 'valid-sacramental-bond-indissoluble', linked: true },
                    { type: 'is extended by', target: 'covenant-breaking-including-abuse', linked: true },
                ],
            },
            {
                id: 'covenant-breaking-including-abuse',
                title: 'Abuse and severe neglect can break a marriage covenant',
                thesis: 'Unrepentant violence, coercive abuse, or severe neglect can constitute covenant-breaking abandonment, so the innocent spouse may seek divorce and, after careful discernment, remarry.',
                accent: 'amber',
                badges: ['Pentecostal', 'Evangelical', 'Moderate', 'Safeguarding'],
                summary: 'This view treats adultery and physical departure as clear examples of a wider reality: one spouse can repudiate the marriage through conduct that destroys its basic obligations and safety. It draws on the biblical duties of provision, fidelity, mutual care, and peace, and on Paul’s principle that a believer is not enslaved when the other spouse abandons the covenant. Immediate safety does not depend on completing a theological process; the later questions of divorce and remarriage require evidence, pastoral accountability, and appropriate civil protection.',
                contribution: 'It addresses forms of abandonment that occur while an abusive spouse remains physically present and refuses responsibility. It also resists making the victim’s departure for safety the act that supposedly broke the marriage.',
                caution: 'Because Scripture does not present every modern category in a single exception clause, the view must explain its inference carefully and avoid making ordinary marital failure equivalent to destructive abuse. Churches also need competent safeguarding practice rather than relying only on informal pastoral judgement.',
                supporters: [
                    { name: 'Assemblies of God (USA)', sourceId: 'ag-divorce-remarriage' },
                    { name: 'David Instone-Brewer', sourceId: 'instone-brewer-divorce' },
                ],
                predicates: [
                    {
                        label: 'A command’s covenant purpose can govern circumstances not named in its ancient form',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'preserve-purpose-adapt-form',
                        linked: true,
                    },
                    {
                        label: 'Holiness is incompatible with violence and includes accountable love of neighbour',
                        topicSlug: 'how-should-holiness-be-recognised',
                        readingId: 'personal-and-social-love',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'extends', target: 'adultery-and-desertion', linked: true },
                    { type: 'contrasts with', target: 'no-remarriage-while-spouse-lives', linked: true },
                    { type: 'overlaps with', target: 'orthodox-pastoral-economia', linked: true },
                ],
            },
            {
                id: 'orthodox-pastoral-economia',
                title: 'Remarriage may be permitted through pastoral mercy',
                thesis: 'Marriage is intended to be lifelong, yet the church may recognise its tragic breakdown and permit a second or third marriage as a penitential concession through pastoral economy.',
                accent: 'teal',
                badges: ['Orthodox', 'Sacramental', 'Pastoral', 'Economia'],
                summary: 'Orthodox teaching presents one enduring marriage as the Christian norm and does not treat divorce as morally neutral. At the same time, a bishop may apply economy—pastoral mercy within the church’s canonical life—when sin or human weakness has destroyed a marriage. A later marriage is permitted rather than celebrated as equivalent to the first, and its liturgical form has traditionally carried a penitential character.',
                contribution: 'It combines a strong theological ideal with a corporate practice of repentance, mercy, and restoration after failure. The approach asks not only whether an exception can be proved but how the church should accompany people truthfully after a marriage has ended.',
                caution: 'Pastoral discretion can vary between jurisdictions and cases, and readers may find it difficult to identify consistent boundaries for economy. The view must show how mercy remains accountable to Jesus’ teaching and does not make permanence merely aspirational.',
                supporters: [
                    { name: 'Orthodox Church in America', sourceId: 'oca-divorce-remarriage' },
                    { name: 'Holy Synod of the Orthodox Church in America', sourceId: 'oca-on-marriage' },
                ],
                predicates: [
                    {
                        label: 'Apostolic teaching is received through the church’s living canonical and liturgical tradition',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'ecclesial-reception',
                        linked: true,
                    },
                    {
                        label: 'Repentance and restoration take shape through participation in the church’s sacramental life',
                        topicSlug: 'how-should-holiness-be-recognised',
                        readingId: 'participation-and-communion',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'valid-sacramental-bond-indissoluble', linked: true },
                    { type: 'overlaps with', target: 'covenant-breaking-including-abuse', linked: true },
                    { type: 'qualifies', target: 'adultery-and-desertion', linked: true },
                ],
            },
        ],
        sources: [
            source(
                'piper-divorce-remarriage',
                'John Piper',
                'Divorce & Remarriage: A Position Paper',
                'An evangelical argument that separation may be necessary but remarriage is prohibited while a former spouse lives.',
                'https://www.desiringgod.org/articles/divorce-and-remarriage-a-position-paper',
            ),
            source(
                'catechism-marriage-offences',
                'Catechism of the Catholic Church',
                'Offenses Against the Dignity of Marriage',
                'Official Catholic teaching on indissolubility, legitimate separation, civil divorce for legal protection, and remarriage while a valid bond remains.',
                'https://www.vatican.va/content/catechism/en/part_three/section_two/chapter_two/article_6/iv_offenses_against_the_dignity_of_marriage.html',
            ),
            source(
                'ratzinger-divorced-remarried',
                'Joseph Ratzinger',
                'Concerning Some Objections to the Church’s Teaching on the Reception of Holy Communion by Divorced and Remarried Members of the Faithful',
                'A Catholic account of indissolubility, declarations of nullity, and the Pauline and Petrine privileges.',
                'https://www.vatican.va/roman_curia/congregations/cfaith/documents/rc_con_cfaith_doc_19980101_ratzinger-comm-divorced_en.html',
            ),
            source(
                'westminster-marriage-divorce',
                'Westminster Assembly',
                'Westminster Confession of Faith, Chapter 24: Of Marriage and Divorce',
                'A classical Reformed statement permitting divorce and remarriage for adultery or irremediable wilful desertion through an orderly process.',
                'https://opc.org/WCF-WIP.html',
            ),
            source(
                'ag-divorce-remarriage',
                'Assemblies of God (USA)',
                'Divorce and Remarriage',
                'An official Pentecostal position recognising adultery, abandonment, domestic violence, and ecclesiastical annulment as exceptions that can permit divorce and remarriage.',
                'https://ag.org/Beliefs/Position-Papers/Divorce-and-Remarriage',
            ),
            source(
                'instone-brewer-divorce',
                'David Instone-Brewer',
                'Divorce and Remarriage in the Bible: The Social and Literary Context',
                'An academic historical study arguing that biblical marital obligations and valid grounds include neglect and abuse as well as adultery and abandonment.',
                'https://www.eerdmans.com/9780802849434/divorce-and-remarriage-in-the-bible/',
            ),
            source(
                'oca-divorce-remarriage',
                'Orthodox Church in America',
                'Divorce and Remarriage',
                'An official Orthodox explanation of lifelong marriage and the possible permission of second or third marriages through pastoral economy.',
                'https://www.oca.org/questions/sacramentmarriage/divorce-and-remarriage',
            ),
            source(
                'oca-on-marriage',
                'Holy Synod of the Orthodox Church in America',
                'On Marriage',
                'A synodal statement presenting one marriage as the norm while describing penitential remarriage and pastoral mercy after divorce.',
                'https://www.oca.org/holy-synod/encyclicals/on-marriage',
            ),
        ],
        generation,
    },
];
